import React, { useEffect, useRef, useState } from 'react';
import { Button, Container, Message, Icon, Divider, Loader } from 'semantic-ui-react';
import { FluidForm } from '../../shared';
import { useFormSubmissionsClient } from '../submissions/formSubmissions.client';
import { FormProvider } from '../fluidComponents/fluidForm/FormContext';
import { ClassicForm } from '../fluidComponents/fluidForm/layouts/ClassicForm.component';
import { getFormInstance, registerFormInstance, useFormInstanceDispatch, useFormUpdatedListener } from '../fluidComponents/fluidForm/FormInstance.service';
import { InComponentErrorRenderer } from '../components/InComponentErrorRenderer';
const SubmissionDataEditor = ({ submissionInitial, formConfigInitial, onUpdate, }) => {
    const submissionsClient = useFormSubmissionsClient();
    const [submission, setSubmission] = useState(submissionInitial);
    const [formConfigRecord, setFormConfig] = useState(formConfigInitial);
    // Early return if formConfig is not available
    if (!formConfigRecord) {
        return (React.createElement(Message, { warning: true },
            React.createElement(Icon, { name: "warning sign" }),
            "Form configuration not found. Cannot edit submission data."));
    }
    //Get form config from submission versionsId 
    const formConfig = formConfigRecord.versions?.find(v => v.versionId === submission.formVersionId)?.formConfig || formConfigRecord.formConfig;
    let formInstance = useRef(registerFormInstance({ form: new FluidForm(formConfig, {}, {}) })).current;
    let dispatch = useFormInstanceDispatch(formInstance);
    useFormUpdatedListener(formInstance, () => {
        setShowSuccess(false); // Hide success message when user makes changes
        setError(null); // Clear any previous errors
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        dispatch("setSubmissionData", submission.data);
    }, [submission.data]);
    const handleSubmit = async (e) => {
        // Prevent any default behavior that might cause page jumping
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setIsSubmitting(true);
        setError(null);
        try {
            const submissionData = getFormInstance(formInstance).getFormSubmission();
            await submissionsClient.updateSubmission(submission.submissionId, {
                data: submissionData,
                formKey: formConfigRecord.formKey
            });
            // Show success state
            setShowSuccess(true);
            // Notify parent component of the update
            if (onUpdate) {
                const updatedSubmission = {
                    ...submission,
                    data: submissionData,
                    lastUpdated: Date.now()
                };
                onUpdate(updatedSubmission);
            }
            // Hide success message after 3 seconds
            setTimeout(() => {
                setShowSuccess(false);
            }, 3000);
        }
        catch (error) {
            setError('Failed to update submission. Please try again.');
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleCancel = () => {
        // Reset form to original data
        getFormInstance(formInstance).setSubmissionData(submission.data);
        setShowSuccess(false);
        setError(null);
    };
    if (!formInstance) {
        return React.createElement(Loader, { active: true, inline: "centered", content: "Loading form..." });
    }
    return (React.createElement(Container, { fluid: true },
        showSuccess && (React.createElement(Message, { positive: true, style: { marginBottom: '1rem' } },
            React.createElement(Icon, { name: "check circle" }),
            "Submission updated successfully!")),
        error && React.createElement(InComponentErrorRenderer, { error: error }),
        React.createElement(FormProvider, { formInstance: formInstance },
            React.createElement("div", { style: { marginBottom: '2rem' } },
                React.createElement(ClassicForm, null))),
        React.createElement(Divider, null),
        React.createElement("div", { style: {
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '1rem',
                padding: '1rem 0',
                borderTop: '1px solid #e0e0e0',
                marginTop: '1rem',
                backgroundColor: '#fafafa'
            } },
            React.createElement(Button, { basic: true, type: "button", onClick: handleCancel, disabled: isSubmitting, icon: "cancel", content: "Cancel" }),
            React.createElement(Button, { primary: true, type: "button", onClick: handleSubmit, loading: isSubmitting, disabled: isSubmitting, icon: showSuccess ? "check" : "save", content: showSuccess ? "Saved" : "Save Changes" }))));
};
export default SubmissionDataEditor;
